import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import "./ProfileSection.css";
import { toast } from "react-toastify";
import { MdAssessment, MdBusiness } from "react-icons/md";
import { CheckCircle2 } from "lucide-react";



const ProfileSection = ({ employee, onProfileUpdate }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completion, setCompletion] = useState(0);

  // Get profile picture URL with fallback
  const getProfilePicture = () => {
    // First check if profile API has the picture
    if (profile?.profilePic) {
      return profile.profilePic;
    }
    // Then check if employee data has profilePicture
    if (employee?.profilePicture) {
      return `${API_BASE_URL}${employee.profilePicture}`;
    }
    return null;
  };

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(" ").map((n) => n[0]).join("").toUpperCase();
  };

  // Fetch profile data - FIXED with null check
  useEffect(() => {
    const fetchProfile = async () => {
      // Check if employee exists and has _id
      if (!employee?._id) {
        console.log("⚠️ ProfileSection: Employee ID is undefined, skipping profile fetch");
        setLoading(false);
        return;
      }

      try {
        console.log("🔄 ProfileSection: Fetching profile for employee:", employee._id);
        const res = await axios.get(
          `${API_BASE_URL}/api/profiles/${employee._id}`
        );
        setProfile(res.data);
      } catch (err) {
        console.error("Error fetching profile:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [employee?._id]); // Use optional chaining

  // Fetch profile completion % - FIXED with null check
  useEffect(() => {
    const fetchCompletion = async () => {
      // Check if employee exists and has _id
      if (!employee?._id) {
        console.log("⚠️ ProfileSection: Employee ID is undefined, skipping completion fetch");
        return;
      }

      try {
        console.log("🔄 ProfileSection: Fetching completion for employee:", employee._id);
        const res = await axios.get(
          `${API_BASE_URL}/api/profile-completion/${employee._id}`
        );
        if (res.data && typeof res.data.percentage === 'number') {
          setCompletion(res.data.percentage);
        }
      } catch (err) {
        console.error("Error fetching profile completion:", err);
        // Don't set error state, just log it
      }
    };

    fetchCompletion();
    const interval = setInterval(fetchCompletion, 5000);
    return () => clearInterval(interval);
  }, [employee?._id]); // Use optional chaining

  // Don't render if no employee
  if (!employee) {
    console.log("⚠️ ProfileSection: No employee data");
    return (
      <div className="ps-container">
        <div className="ps-loading-state">
          <p>Loading employee information...</p>
        </div>
      </div>
    );
  }

  if (loading) return <div className="ps-loading-spinner"></div>;

  const profilePicture = getProfilePicture();

  return (
    <div className="ps-container">
      {/* Avatar Section */}
      <div className="ps-avatar-section">
        <div className="ps-avatar-container">
          {profilePicture ? (
            <img
              src={profilePicture} 
              alt="Profile"
              className="ps-avatar-img"
            />
          ) : (
            <div className="ps-avatar-placeholder">
              {getInitials(employee.name || "Employee")}
            </div>
          )}
          <label htmlFor="ps-upload-input" className="ps-upload-label">
            <span className="ps-upload-icon">+</span>
            Change Photo
          </label>
          <input
            id="ps-upload-input"
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file || !employee?._id) return;
              
              // Validate file type and size
              if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return;
              }
              if (file.size > 5 * 1024 * 1024) { // 5MB limit
                toast.error('Image size should be less than 5MB');
                return;
              }

              const formData = new FormData();
              formData.append("profilePic", file);
              
              try {
                const res = await axios.put(
                  `${API_BASE_URL}/api/profiles/${employee._id}/profile-picture`,
                  formData,
                  { 
                    headers: { 
                      "Content-Type": "multipart/form-data",
                    }
                  }
                );
                
                // Update profile state
                setProfile(res.data);
                
                // Update employee data in sessionStorage with new profile picture
                const updatedEmployee = {
                  ...employee,
                  profilePicture: res.data.profilePicture || employee.profilePicture
                };
                sessionStorage.setItem("employee", JSON.stringify(updatedEmployee));
                
                // Call the callback to update parent component
                if (onProfileUpdate) {
                  onProfileUpdate(updatedEmployee);
                }
                
                // Refresh completion
                const completionRes = await axios.get(
                  `${API_BASE_URL}/api/profile-completion/${employee._id}`
                );
                setCompletion(completionRes.data.percentage);
                
                // Show success message
                toast.success('Profile picture updated successfully! ✅');
                
                // Force refresh to update all components
                window.location.reload();
                
              } catch (err) {
                console.error("Error uploading profile picture:", err);
                toast.error("Failed to update profile picture. Please try again.");
              }
            }}
            className="ps-upload-input"
          />
        </div>

        <div className="ps-basic-info">
          <h3 className="ps-employee-name">{employee.name || "Employee"}</h3>
          <p className="ps-employee-role">
            {employee.position || employee.role || "No position specified"}
          </p>
         <p className="ps-employee-department">
            <span className="ps-department-icon">
              <MdBusiness />
            </span>
            {employee.department}
          </p>
        </div>
      </div>

      {/* Progress Section */}
      <div className="ps-progress-section">
        <h4 className="ps-progress-title">
          <span className="ps-progress-icon">
           <MdAssessment />
          </span>Profile Completion
        </h4>
      <div className="ps-progress-container">
          <div className="ps-progress-bar">
            <div
              className="ps-progress-fill"
              style={{ width: `${completion}%` }}
            ></div>
          </div>
          <span className="ps-progress-percentage">{completion}%</span>
        </div>

        {completion === 100 ? (
          <p className="ps-progress-text success">
              <CheckCircle2 size={18} />
               Your profile is complete!
          </p>
        ) : (
          <p className="ps-progress-text">
            Complete all sections to reach 100%
          </p>
        )}
      </div>
    </div>
  );
};

export default ProfileSection;